// see https://nextjs.org/docs/advanced-features/custom-app for documentation on how to use this next.js feature

// Context: Adding these polyfill library's to support Intl.DateTimeFormat in the older browsers
// https://app.asana.com/0/1201332405126688/1207272020910746/f
import "@formatjs/intl-datetimeformat/add-all-tz";
import "@formatjs/intl-datetimeformat/locale-data/en";
import "@formatjs/intl-datetimeformat/polyfill";

import { Variant } from "@amplitude/experiment-node-server";
import flowRight from "lodash/flowRight";
import { appWithTranslation, SSRConfig } from "next-i18next";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";

import { buildCanonicalUrlFromPath } from "lib/utils/buildCanonicalUrlFromPath";

import "styles/global.scss";

import i18n, { useTranslation } from "components/i18n/source";
import MaintenancePage from "components/pages/maintenance/MaintenancePage";
import { MaintenancePagePayload } from "components/pages/maintenance/types";
import { withExperimentsContext } from "components/store/source/context/ExperimentsContext";

import useHandleLogoutAnd4FA from "hooks/useHandleLogoutAnd4FA";
import { AmplitudeFlag, Bootstrap } from "types/bootstrap";

interface HeaderProps {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
}

type PageProps = {
  experiments?: Record<string, Variant>;
  headerProps: HeaderProps;
  bootstrap?: Bootstrap;
} & SSRConfig;

function App({ Component, pageProps }: AppProps<PageProps>) {
  const { locale } = useRouter();

  useHandleLogoutAnd4FA();

  // Make sure our i18next client is initialized with the correct locale
  i18n.changeLanguage(locale);

  const maintenancePageFlag = pageProps?.bootstrap?.featureFlags
    ?.maintenancePageEnabled as AmplitudeFlag;

  if (maintenancePageFlag && maintenancePageFlag?.value !== "off") {
    const { heading, description, contactLine } =
      maintenancePageFlag?.payload as MaintenancePagePayload;

    return (
      <MaintenancePage
        heading={heading}
        description={description}
        contactLine={contactLine}
      />
    );
  }

  return (
    <>
      <Header {...pageProps.headerProps} />
      <Component {...pageProps} />
    </>
  );
}

function Header(props: HeaderProps) {
  const { t } = useTranslation();
  const defaultHeaderProps: HeaderProps = {
    title: t("header.title"),
    description: t("header.description"),
    ogTitle: t("header.og.title"),
    ogDescription: t("header.og.description"),
    ogImage:
      "https://assets.sonder.com/assets/www/sonder-green-6cf7bab8f6abbe7ed6740ab2f7628c0d4cc75244ad4e3f7c85d778237465905d.png",
  };
  const headerProps = Object.assign({}, defaultHeaderProps, props);
  const { locales, locale, asPath } = useRouter();

  return (
    <Head>
      <title>{headerProps.title}</title>
      <meta name="description" content={headerProps.description} />
      <meta property="og:title" content={headerProps.ogTitle} />
      <meta property="og:description" content={headerProps.ogDescription} />
      <meta property="og:image" content={headerProps.ogImage} />
      <meta
        property="og:url"
        content={buildCanonicalUrlFromPath(asPath, locale)}
      />

      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0"
      />

      {/* https://developers.google.com/search/docs/advanced/crawling/consolidate-duplicate-urls#rel-canonical-link-method */}
      <link rel="canonical" href={buildCanonicalUrlFromPath(asPath, locale)} />

      {/* https://developers.google.com/search/docs/advanced/crawling/localized-versions#html */}
      {locales.map(supportedLocale => {
        return (
          <link
            key={supportedLocale}
            rel="alternate"
            hrefLang={supportedLocale.toLowerCase()}
            href={buildCanonicalUrlFromPath(asPath, supportedLocale)}
          />
        );
      })}
    </Head>
  );
}

export default flowRight([appWithTranslation, withExperimentsContext])(App);
